/*       */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Grid, Paper, Typography, MdiIcon, IconButton, Dialog, FormHelperText, } from '@mic3/platform-ui';
import styled from 'styled-components';

import { cut } from 'app/utils/string/string-utils';
import { useToggle, useOnChange } from 'app/utils/hook/hooks';
import Textarea from './Textarea';

const DialogStyled = styled(Dialog)`
    height: 100%;
    overflow: hidden;
    & .MuiDialog-paper {
        padding: 16px;
    }

`;

const StyledIconButton = styled(IconButton)`
    vertical-align: middle;
    cursor: point;
    margin-right: 12px !important;
    color: ${({ theme }) => theme.material.colors.text.secondary} !important;
`;

const Container = styled.div`
   margin: 12px 0 20px;
   position: relative;
 `;

const PaperStyled = styled(Paper)`
    padding: 12px;
    cursor: pointer;
    width: 100%;
    background: ${({ theme }) => theme.material.colors.background.fields} !important;
`;

const TypographyStyled = styled(Typography)`
    ${({ disabled }) => (disabled ? 'color: #95989c !important;' : '')}
`;

const FormHelperTextStyled = styled(FormHelperText)`
    padding-left: 14px;
`;

const TextareaDialog = props => {
    const { value: propValue, name, title, onChange, template, disabled, trimOnSave, button, labelDialogField, buttonLabel, required, iconButtonName,
        ...restProps } = props;
    const [isOpen, toggle] = useToggle();
    const [isValid, setIsValid] = useState(false);
    const [value, onChangeTextarea, setValue] = useOnChange(propValue);
    const [fullScreen, onChangeFullScreen] = useOnChange(false);
    useEffect(() => {
        setValue(propValue);
    }, [propValue, setValue]);

    const onSave = useCallback(
        e => {
            onChange({ target: { value: trimOnSave ? value && value.trim() : value, name } });
            toggle();
        },
        [name, onChange, toggle, value, trimOnSave]
    );
    const onValidTextarea = useCallback(valid => {
        setIsValid(valid);
    }, []);
    const textareaValue = useMemo(() => value || template, [template, value, propValue]); // eslint-disable-line

    const preview = useMemo(() => {
        if(!propValue) return '(empty)';
        if(typeof propValue === 'string') return cut(propValue, 32);
        return JSON.stringify(propValue);
    }, [propValue])

    const renderTriggerElement = () => {
        if (button) {
            return (
                <Button variant="text" disabled={disabled} onClick={toggle}>
                    {buttonLabel || title}
                </Button>
            );
        }

        if (iconButtonName) {
            return (
                <StyledIconButton disabled={disabled} onClick={toggle}>
                    <MdiIcon name={iconButtonName} />
                </StyledIconButton>
            );
        }

        return (
            <Container>
                <PaperStyled onClick={toggle}>
                    <Grid container direction="column" alignItems="stretch">
                        <TypographyStyled disabled={disabled} variant="caption">
                            {labelDialogField || title}{required && <span class="MuiFormLabel-asterisk"> *</span>}
                        </TypographyStyled>
                        <TypographyStyled disabled={disabled}>
                            {preview}
                        </TypographyStyled>
                    </Grid>
                </PaperStyled>
                {props.helperText && (
                    <FormHelperTextStyled required={required} error={props.error}>
                        {props.helperText}
                    </FormHelperTextStyled>
                )}
            </Container>
        );
    };


    return (
        <>
            {renderTriggerElement()}
            <DialogStyled
                open={isOpen}
                onClose={toggle}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="md"
            >
                <Grid container justify="space-between" alignItems="center">
                    <Grid item container xs={6} alignItems="center">
                        <IconButton aria-label="close" onClick={toggle}>
                            <MdiIcon name="close" />
                        </IconButton>
                        <Typography variant="h6">{title}</Typography>
                    </Grid>
                    <Grid
                        item
                        container
                        xs={6}
                        justify="flex-end"
                        alignItems="center"
                    >
                        <Checkbox
                            onChange={onChangeFullScreen}
                            value={fullScreen}
                            label="Full screen"
                        />
                        <Button
                            onClick={onSave}
                            disabled={!isValid || disabled}
                            style={{ marginRight: '10px' }}
                        >
                            Apply
                        </Button>
                    </Grid>
                </Grid>
                <Textarea
                    modal
                    {...restProps}
                    disabled={disabled}
                    onValid={onValidTextarea}
                    rows={fullScreen ? 30 : 15}
                    onChange={onChangeTextarea}
                    value={textareaValue}
                    heightOffset={fullScreen ? '240px' : '100px'}
                />
            </DialogStyled>
        </>
    );
};

TextareaDialog.propTypes = {
    ...(Textarea || {}).propTypes,
    title: PropTypes.string.isRequired,
    template: PropTypes.string,
};

export default TextareaDialog;
